import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, ShieldCheck, XCircle, Upload, FileText } from "lucide-react";
import Footer from "@/components/Footer";
import Seo from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const aud = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);

function Header() {
  return (
    <header className="border-b border-[#27272A]">
      <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="font-display text-lg font-bold tracking-tighter">
          Barrett Career <span className="text-[#DFFF00]">Brand</span>
        </Link>
        <Link to="/contact" data-testid="quote-contact-link" className="font-body text-sm text-[#A1A1AA] hover:text-[#DFFF00] transition-colors">
          Questions? Contact us
        </Link>
      </div>
    </header>
  );
}

function PolicyLinks() {
  return (
    <p data-testid="quote-policy-links" className="mt-6 font-body text-sm text-[#52525B] leading-relaxed">
      Before you pay, you can read our{" "}
      <Link to="/terms" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">service terms</Link>,{" "}
      <Link to="/privacy" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">privacy policy</Link> and{" "}
      <Link to="/refunds" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">refunds and complaints information</Link>.
      Nothing in our terms removes your rights under Australian Consumer Law.
    </p>
  );
}

function SummaryCard({ q }) {
  const rows = [
    ["Total price", aud(q.amount_aud)],
    ["Tax", q.gst_line],
    ["Delivery timeframe", q.delivery_timeframe || "—"],
    ["Included revisions", q.revisions || "—"],
  ];
  return (
    <div className="border border-[#27272A] bg-[#141414] p-6 md:p-8">
      <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00]">Quote {q.reference}</p>
      <h2 data-testid="quote-service-name" className="mt-3 font-display text-2xl md:text-3xl font-bold tracking-tight">{q.service_label}</h2>
      <p className="mt-2 font-body text-sm text-[#A1A1AA]">Prepared for {q.name}</p>
      <ul className="mt-6 space-y-3">
        {q.inclusions.map((it, i) => (
          <li key={i} className="flex gap-3 font-body text-[16px] text-[#D4D4D8] leading-[1.6]">
            <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-[#DFFF00] flex items-center justify-center">
              <Check className="text-[#0A0A0A]" size={12} />
            </span>
            {it}
          </li>
        ))}
      </ul>
      {q.notes && <p className="mt-5 font-body text-[15px] text-[#A1A1AA] leading-[1.6]">{q.notes}</p>}
      <div className="mt-6 border-t border-[#27272A] pt-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-6 py-2.5 border-b border-[#27272A]/60">
            <span className="font-body text-sm text-[#52525B]">{k}</span>
            <span data-testid={`quote-${k.toLowerCase().replace(/ /g, "-")}`} className="font-body text-sm text-[#F8F8F5] text-right font-semibold">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrderView({ q, token, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/quotes/${token}/documents`, fd);
      onUpdated(r.data);
      toast.success("File uploaded securely. George has been notified.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div data-testid="order-confirmed-view">
      <div className="w-14 h-14 rounded-full bg-[#DFFF00] flex items-center justify-center">
        <Check className="text-[#0A0A0A]" size={26} />
      </div>
      <h1 className="mt-6 font-display text-3xl md:text-4xl font-bold tracking-tighter">Order confirmed. Thank you, {q.name.split(" ")[0]}.</h1>
      <p className="mt-3 font-body text-[17px] text-[#A1A1AA] leading-[1.6]">
        Your payment of <span className="text-[#F8F8F5] font-semibold">{aud(q.amount_aud)}</span> for{" "}
        <span className="text-[#F8F8F5] font-semibold">{q.service_label}</span> is confirmed (order {q.reference}).
        A receipt and confirmation email are on their way to you.
      </p>

      <div className="mt-8 border border-[#27272A] bg-[#141414] p-6 md:p-8">
        <h3 className="font-display text-xl font-bold">What happens next</h3>
        <ol className="mt-4 space-y-3 font-body text-[16px] text-[#D4D4D8] leading-[1.6] list-decimal pl-5">
          <li>Upload your CV or any relevant documents below — this page is your secure channel, so there is no need to email attachments.</li>
          <li>George will review what you send and contact you by email to get started.</li>
          <li>Your work is delivered {q.delivery_timeframe ? `within ${q.delivery_timeframe.toLowerCase()}` : "within the agreed timeframe"} and includes {q.revisions ? q.revisions.toLowerCase() : "the agreed revisions"}.</li>
        </ol>
      </div>

      <div className="mt-6 border border-[#27272A] bg-[#141414] p-6 md:p-8">
        <h3 className="font-display text-xl font-bold">Send your documents securely</h3>
        <p className="mt-2 font-body text-sm text-[#A1A1AA]">PDF, Word or text files, up to 10 MB each.</p>
        {q.documents?.length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="order-documents-list">
            {q.documents.map((d, i) => (
              <li key={i} className="flex items-center gap-2 font-body text-sm text-[#D4D4D8]">
                <FileText size={15} className="text-[#DFFF00]" /> {d.original_filename} ({Math.max(1, Math.round(d.size / 1024))} KB)
              </li>
            ))}
          </ul>
        )}
        <input
          ref={inputRef} type="file" accept=".pdf,.doc,.docx,.txt" className="hidden"
          data-testid="order-upload-input"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <button
          data-testid="order-upload-btn"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="mt-5 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-3.5 inline-flex items-center gap-2 hover:bg-[#C8E600] transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 className="animate-spin" size={17} /> : <Upload size={17} />}
          {uploading ? "Uploading…" : "Upload a file"}
        </button>
      </div>

      <p className="mt-6 font-body text-sm text-[#52525B] leading-relaxed">
        Need help with anything? Reply to your confirmation email or use the{" "}
        <Link to="/contact" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">contact page</Link>.
        Our <Link to="/refunds" className="text-[#A1A1AA] underline underline-offset-4 hover:text-[#DFFF00]">refunds and complaints information</Link> explains your options.
      </p>
    </div>
  );
}

export default function Quote() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound
  const [paying, setPaying] = useState(false);
  const cancelled = params.get("cancelled") === "1";

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/quotes/${token}`)
      .then((r) => { setQ(r.data); setState("ready"); })
      .catch(() => setState("notfound"));
  }, [token]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/quotes/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We could not start the payment page. Nothing was charged — please try again.");
      setPaying(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#DFFF00]" size={28} />
      </div>
    );
  }

  return (
    <main data-testid="quote-page" className="bg-[#0A0A0A] text-[#F8F8F5] min-h-screen flex flex-col">
      <Seo title="Your quote — Barrett Career Brand" description="Review and accept your quote from Barrett Career Brand." path={`/quote/${token}`} />
      <Header />
      <div className="flex-1 max-w-2xl w-full mx-auto px-6 py-12 md:py-16">
        {state === "notfound" && (
          <div data-testid="quote-notfound" className="text-center py-16">
            <h1 className="font-display text-3xl font-bold tracking-tight">We couldn't find that quote.</h1>
            <p className="mt-4 font-body text-[#A1A1AA] leading-relaxed">
              Please check the link in your email, or <Link to="/contact" className="text-[#DFFF00] underline underline-offset-4">contact us</Link> and we will send it again.
            </p>
          </div>
        )}

        {state === "ready" && q.status === "cancelled" && (
          <div data-testid="quote-cancelled-view" className="text-center py-16">
            <XCircle className="text-[#A1A1AA] mx-auto" size={36} />
            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">This quote is no longer available.</h1>
            <p className="mt-4 font-body text-[#A1A1AA] leading-relaxed">
              No payment has been taken. If you would still like to go ahead,{" "}
              <Link to="/contact" className="text-[#DFFF00] underline underline-offset-4">contact us</Link> for an updated quote.
            </p>
          </div>
        )}

        {state === "ready" && q.status === "paid" && (
          <OrderView q={q} token={token} onUpdated={setQ} />
        )}

        {state === "ready" && q.status === "sent" && (
          <>
            {cancelled && (
              <div data-testid="quote-cancelled-banner" className="mb-6 flex items-start gap-3 border border-[#27272A] bg-[#141414] px-5 py-4 font-body text-sm text-[#D4D4D8] leading-relaxed">
                <XCircle size={18} className="text-[#A1A1AA] shrink-0 mt-0.5" />
                <span>Payment was cancelled and <strong className="text-[#F8F8F5]">nothing was charged</strong>. Your quote is still here whenever you're ready — or <Link to="/contact" className="text-[#DFFF00] underline underline-offset-4">contact us</Link> with any questions.</span>
              </div>
            )}
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter">Review and accept your quote</h1>
            <p className="mt-3 font-body text-[16px] text-[#A1A1AA] leading-[1.6]">
              Take your time — you will only be charged if you choose to accept and pay.
            </p>
            <div className="mt-8">
              <SummaryCard q={q} />
            </div>
            <button
              data-testid="quote-accept-pay-btn"
              disabled={paying}
              onClick={pay}
              className="mt-8 w-full sm:w-auto rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-[#C8E600] transition-colors disabled:opacity-60"
            >
              {paying ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
              {paying ? "Opening secure payment…" : `Accept and pay ${aud(q.amount_aud)}`}
              {!paying && <ArrowRight size={18} />}
            </button>
            <p className="mt-4 font-body text-sm text-[#52525B] leading-relaxed">
              Payment is handled by Stripe on a secure page. We never ask for card details by email, phone or chat.
            </p>
            <PolicyLinks />
          </>
        )}
      </div>
      <Footer />
    </main>
  );
}
